import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';

import _ from 'lodash';
import { ButtonGroup, ToggleButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import { useMessage, MessageActionType } from 'common/message-bar/MessageBar';
import { MetaData } from 'common/response-editor/MetadataParser';
import { MediaType } from 'common/upload/Upload';
import { Overlay } from 'core/overlay/Overlay';
import { Switch } from 'core/switch/Switch';
import { Collage, CollageGallery } from 'social/common/collage/Collage';
import { getFirstLinkFromMetaData, hasLinkInMetaData, getFirstLinkIndexFromMetaData } from 'social/common/util/Util';
import { ErrorDisplay } from 'social/publisher/error-display/ErrorDisplay';
import {
    ExtractorSource,
    PostType,
    PublisherError,
    SelectedSource,
    SourceName,
    UrlMetaData,
} from 'social/publisher/Publisher.types';
import { usePublisherDispatch } from 'social/publisher/PublisherContext';
import { fetchShortenUrl, extractLinksMetadata, fetchUrlMetaData } from 'social/publisher/PublisherFetchers';

import 'social/publisher/post-type-selector/PostTypeSelector.scss';

interface Props {
    content: MetaData[];
    hasUserChosenMediaPostType: boolean;
    postType: PostType;
    tenantId: number | undefined;
    urlMetaData: UrlMetaData | null;
    selectedSources: SelectedSource;
    publisherErrors: PublisherError[];
    responseEditorRef: any;
    customizeSourceKey: SourceName | 'ALL_SOURCES';
    isShortenedUrl: boolean;
}
const OVERLAY_AREA = 'social-publisher-posttype-selector';

export const PostTypeSelector: FunctionComponent<Props> = ({
    content,
    hasUserChosenMediaPostType,
    postType,
    selectedSources,
    tenantId,
    urlMetaData,
    publisherErrors,
    responseEditorRef,
    customizeSourceKey,
    isShortenedUrl,
}) => {
    const dispatch = usePublisherDispatch();
    const messageDispatch = useMessage()[1];
    const { t } = useTranslation('social/publisher');
    const [originalUrlContent, setOriginalUrlContent] = useState<MetaData | null>(null);
    const [shortenedUrl, setShortenedurl] = useState('');
    const convertUrlMetaDataToGallery = (urlMetaData: UrlMetaData): CollageGallery[] => {
        let gallery: CollageGallery[] = [];

        if (!_.isEmpty(urlMetaData.images)) {
            gallery = urlMetaData.images.map((image) => {
                return {
                    url: image.url,
                    type: MediaType.Image,
                };
            });
        }

        return gallery;
    };
    const handleSelectLinkPostType = (): void => {
        dispatch({ type: 'set-post-type', payload: { value: 'LINK' } });
        dispatch({ type: 'set-has-user-chosen-media-post-type', payload: { value: false } });
    };
    const handleSelectMediaPostType = (): void => {
        dispatch({ type: 'set-post-type', payload: { value: 'MEDIA' } });
        dispatch({ type: 'set-has-user-chosen-media-post-type', payload: { value: true } });
    };
    const postTypeOptions = [
        {
            value: 'LINK',
            label: 'LINK_POST_TYPE',
            handler: handleSelectLinkPostType,
        },
        {
            value: 'MEDIA',
            label: 'MEDIA_POST_TYPE',
            handler: handleSelectMediaPostType,
        },
    ];
    const url: string | undefined = getFirstLinkFromMetaData(content);
    const hasLink = hasLinkInMetaData(content);
    const shouldShowPostTypeOptions =
        (customizeSourceKey !== 'ALL_SOURCES' &&
            customizeSourceKey !== 'INSTAGRAM' &&
            customizeSourceKey !== 'GOOGLE_PLACES' &&
            selectedSources[customizeSourceKey] &&
            hasLink) ||
        ((selectedSources.FACEBOOK || selectedSources.TWITTER || selectedSources.LINKEDIN) &&
            !selectedSources.INSTAGRAM &&
            !selectedSources.GOOGLE_PLACES &&
            customizeSourceKey === 'ALL_SOURCES' &&
            hasLink);
    const shouldShowLinkPreview = shouldShowPostTypeOptions && !hasUserChosenMediaPostType;
    const setUrlMetaDataAndPostType = useCallback(async () => {
        if (shouldShowLinkPreview) {
            let urlMetaData: UrlMetaData[] = [];

            dispatch({ type: 'set-post-type', payload: { value: 'LINK' } });
            dispatch({ type: 'add-promises-in-transit', payload: { value: 'LINK' } });

            // remove the back up api call once core's service is verified
            try {
                try {
                    urlMetaData = await trackPromise(
                        extractLinksMetadata(tenantId, [
                            { link: { url: url as string }, extractorSource: ExtractorSource.Embedly },
                        ]),
                        OVERLAY_AREA,
                    );
                } catch {} // let it fail silently, try social's api

                if (_.isEmpty(urlMetaData)) {
                    try {
                        urlMetaData = await trackPromise(fetchUrlMetaData(tenantId, url as string), OVERLAY_AREA);
                    } catch (err) {
                        const errMsg = _.get(err, 'response.data.error.detail');

                        messageDispatch({
                            type: MessageActionType.ShowError,
                            text: errMsg || 'Link is invalid. Please enter a valid link.',
                        });
                    }
                }
            } finally {
                if (!_.isEmpty(urlMetaData)) {
                    dispatch({
                        type: 'set-url-meta-data',
                        payload: { value: urlMetaData[0] },
                    });
                } else {
                    dispatch({
                        type: 'set-url-meta-data',
                        payload: { value: null },
                    });
                }
                dispatch({ type: 'remove-promises-in-transit', payload: { value: 'LINK' } });
            }
        } else {
            dispatch({ type: 'set-post-type', payload: { value: 'MEDIA' } });
            dispatch({
                type: 'set-url-meta-data',
                payload: { value: null },
            });
            dispatch({ type: 'set-is-shortened-url', payload: { value: false } });
        }
    }, [dispatch, messageDispatch, tenantId, url, shouldShowLinkPreview]);

    useEffect(() => {
        setUrlMetaDataAndPostType();
    }, [setUrlMetaDataAndPostType]);

    const setFirstLinkInContent = (linkObj: { link: string; text?: string }): void => {
        const linkContentIndex = getFirstLinkIndexFromMetaData(content);
        const linkContent = content[linkContentIndex];
        const linkContentText = linkContent.text;
        const linkContentLink = _.get(linkContent, 'attributes.link');
        const newLinkContent = _.cloneDeep(linkContent);
        const newContent = [...content];

        _.assign(newLinkContent.attributes, { link: linkObj.link });

        if (linkObj.text) {
            newLinkContent.text = linkObj.text;
        } else {
            const urlWithoutProtocols = linkContentLink.replace(/^https?:\/\//, '');

            if (linkContentText.indexOf(urlWithoutProtocols) > -1) {
                newLinkContent.text = linkObj.link;
            }
        }

        newContent[linkContentIndex] = newLinkContent;

        dispatch({ type: 'set-content', payload: { value: newContent } });
        if (responseEditorRef && responseEditorRef.current) {
            if (responseEditorRef.current.setContents) {
                responseEditorRef.current.setContents(newContent);
            }
        }
    };

    const handleToggleShortenUrl = async (): Promise<void> => {
        if (isShortenedUrl) {
            dispatch({ type: 'set-is-shortened-url', payload: { value: false } });
            if (originalUrlContent) {
                setFirstLinkInContent({
                    link: _.get(originalUrlContent, 'attributes.link'),
                    text: originalUrlContent.text,
                });
            }
        } else {
            const firstLinkIndex = getFirstLinkIndexFromMetaData(content);

            if (firstLinkIndex > -1) {
                setOriginalUrlContent(_.cloneDeep(content[firstLinkIndex]));

                dispatch({ type: 'add-promises-in-transit', payload: { value: 'SHORTEN_LINK' } });
                try {
                    const shortenedUrl = await trackPromise(fetchShortenUrl(tenantId, url as string), OVERLAY_AREA);

                    setShortenedurl(shortenedUrl);
                    setFirstLinkInContent({
                        link: shortenedUrl,
                    });
                    dispatch({ type: 'set-is-shortened-url', payload: { value: true } });
                } catch (err) {
                    // need to verify this path is where the error message is, was not able to trigger an error
                    const errMsg = _.get(err, 'response.data.error.detail');

                    messageDispatch({
                        type: MessageActionType.ShowError,
                        text: errMsg || 'Failed to shorten the link.',
                    });
                } finally {
                    dispatch({ type: 'remove-promises-in-transit', payload: { value: 'SHORTEN_LINK' } });
                }
            }
        }
    };

    const originalUrl = _.get(originalUrlContent, 'attributes.link');

    useEffect(() => {
        if (url && originalUrl && shortenedUrl) {
            const removeProtocolsFromUrl = (url: string): string => {
                return url.replace(/^https?:\/\//, '');
            };

            if (
                removeProtocolsFromUrl(url) !== removeProtocolsFromUrl(originalUrl) &&
                removeProtocolsFromUrl(url) !== removeProtocolsFromUrl(shortenedUrl)
            ) {
                dispatch({ type: 'set-is-shortened-url', payload: { value: false } });
            }
        }
    }, [url, originalUrl, shortenedUrl, dispatch]);

    return (
        <div className="r4e-social-post-type-selector">
            <Overlay area={OVERLAY_AREA} />
            <ErrorDisplay publisherErrors={publisherErrors} component="LINK" />
            {shouldShowLinkPreview && urlMetaData && (
                <div className="link-preview">
                    {urlMetaData.images && urlMetaData.images.length > 0 ? (
                        <div className="link-image">
                            <Collage
                                galleryData={convertUrlMetaDataToGallery(urlMetaData).slice(0, 1)}
                                maxCount={1}
                                openModalOnMediaClick={false}
                            />
                        </div>
                    ) : (
                        <></>
                    )}
                    <div className="link-info">
                        <div className="link-title">{urlMetaData.title}</div>
                        <div className="link-description">{urlMetaData.description}</div>
                        <div className="link-url">
                            <a target="_blank" rel="noopener noreferrer" href={url}>
                                {url}
                            </a>
                        </div>
                    </div>
                </div>
            )}
            {shouldShowPostTypeOptions && (
                <div className="post-type-options">
                    <div className="post-type-toggle">
                        <ButtonGroup toggle>
                            {postTypeOptions.map((option) => (
                                <ToggleButton
                                    key={option.value}
                                    type="radio"
                                    className={`btn-post-type-${option.value.toLowerCase()}`}
                                    variant="default"
                                    name="postType"
                                    value={option.value}
                                    checked={option.value === postType}
                                    onChange={option.handler}
                                >
                                    {t(option.label)}
                                </ToggleButton>
                            ))}
                        </ButtonGroup>
                    </div>
                    <div className="shorten-link-toggle">
                        {postType === 'LINK' && (
                            <Switch
                                id="r4e-custom-switch"
                                label={t('SHORTEN_LINK')}
                                checked={isShortenedUrl}
                                onChange={handleToggleShortenUrl}
                            />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
